/* eslint-disable max-lines-per-function */
import React from "react";
import { Link, Routes, Route } from "react-router-dom";
import { Column, FormRow as Row } from "components/common/html";
import { Breadcrumbs, Crumb } from "components/breadcrumbs/breadcrumbs";
import "./support.css";

const WaitListPage: React.FC = () => (
  <>
    <section>
      <h4>How can Haven Connect help me manage my waitlist?</h4>

      <p>
        Haven Connect allow property managers and applicants to update their applications
        electronically. Property managers can also send text messages, emails, and letters to
        clients via Haven Connect.
      </p>
    </section>
  </>
);

const SecurityPage: React.FC = () => (
  <>
    <section>
      <h4>How secure is Haven Connect?</h4>

      <p>
        We take extreme precaution with the data of your applicants. Citizenship and immigration
        information (including Social Security Numbers) is encrypted at rest, which means that even
        if a hacker got into our database, they would be unable to read or decrypt citizenship
        information. Furthermore, all applications are encrypted in transit over a secure SSL
        connection.
      </p>
    </section>

    <section>
      <h4>How do you work to reduce fraud?</h4>

      <p>
        Every applicant is authenticated via email or text prior to electronically signing, which
        means that there is a secondary means of identifying an applicant - something that a regular
        wet signature can not do.
      </p>
    </section>
  </>
);

const ApplicationsPage: React.FC = () => (
  <>
    <section>
      <h4>How much do paper applications cost my organization?</h4>
      <p>
        For a standard first-come, first-serve open waitlist it takes around 10 hours/week or about
        $13,000 for a property manager to:
      </p>
      <ul>
        <li>Receive paper applications</li>
        <li>Do the data entry</li>
        <li>Call down the waitlist</li>
        <li>Send out denial letters</li>
        <li>Field calls from social workers and applicants</li>
        <li>Receive walk-in questions from prospective tenants</li>
      </ul>
      <p>Lease-ups and lotteries are even more expensive.</p>
    </section>
    <section>
      <h4>Can the application be translated into other languages?</h4>

      <p>
        Yes - right now we support Spanish language applications and can include other languages for
        an additional cost.
      </p>
    </section>
  </>
);

const HowItWorksPage: React.FC = () => (
  <>
    <section>
      <h4>Most of my clients are not internet-savvy. How are they supposed to apply?</h4>

      <p>
        Haven Connect does not eliminate paper applications - instead, we integrate with paper to
        allow all applicants to choose their preferred method of submission. Applicants who are not
        digitally savvy can apply with a paper application, and the property manager can upload that
        paper application to get digitized by Haven Connect. Every paper application is then
        integrated with your online applications. Adding an online application just allows those who
        are able to apply online to do so. We find that many applicants who may not be comfortable
        using an online application on their own are assisted by case managers, family or friends.
      </p>
    </section>
    <section>
      <h4>What happens when a client applies online?</h4>

      <p>
        The property manager instantly receives the application in their Haven Connect portal. The
        client is also texted or emailed with confirmation that their application was received.
      </p>
    </section>

    <section>
      <h4>How quickly can my property be up and running on Haven Connect?</h4>

      <p>
        As soon as you send us your property’s compliance-approved application and logo, we will
        transform that into an online application and embed the “apply now” button on your website.
        Then you’re ready to go! This can take as little as a few days to be fully operational.
      </p>
    </section>

    <section>
      <h4>Do you have any special features for seniors?</h4>

      <p>
        Yes. Haven Connect allows seniors to increase the font size and contrast so it is easier for
        them to view and navigate through the application.
      </p>
    </section>

    <section>
      <h4>Are electronic signatures allowed by California’s Tax Credit Allocation Committee?</h4>
      <p>
        Yes. California’s Tax Credit Allocation Committee allows electronic signatures on housing
        applications. Our founder, Caroline Caselli, successfully advocated for this in November
        2015.
      </p>
    </section>

    <section>
      <h4>Are electronic signatures allowed by HUD (Housing and Urban Development)?</h4>

      <p>
        The tenant application is considered an owner/agent form, so provided that your property
        complies with Section 4-14 of HUD Handbook 4350.3, you can use an electronic signature on
        your housing application. Provided your compliance team has ensured that your application
        follows all Fair Housing and applicable laws, Haven Connect just digitizes your form and
        allows it be filled out online.
      </p>
    </section>
  </>
);

const SupportHome: React.FC = () => (
  <>
    <h3 className="lt">Applications</h3>
    <ApplicationsPage />

    <h3 className="lt">How it Works</h3>
    <HowItWorksPage />

    <h3 className="lt">Security</h3>
    <SecurityPage />

    <h3 className="lt">Waitlist</h3>
    <WaitListPage />
  </>
);

const Support: React.FC<{ path?: string }> = () => (
  <div className="Support">
    <Breadcrumbs rootUrl="/support" rootName="Support">
      <Crumb path="security" text="Security" />
      <Crumb path="applications" text="Applications" />
      <Crumb path="how-it-works" text="How It Works" />
      <Crumb path="waitlist" text="Waitlist" />
    </Breadcrumbs>

    <div className="page-container max-960 legacy-bs-override" style={{ paddingTop: 0 }}>
      <Row>
        <Column width={4}>
          <div className="card">
            <h4 style={{ marginTop: -1 }}>Questions by Subject</h4>

            <ul className="no-style">
              <li>
                <Link to="security">Security</Link>
              </li>
              <li>
                <Link to="applications">Applications</Link>
              </li>
              <li>
                <Link to="how-it-works">How it Works</Link>
              </li>
              <li>
                <Link to="waitlist">Waitlist</Link>
              </li>
            </ul>

            <h4>Contact Haven Connect</h4>
            <ul className="no-style">
              <li>
                <a href="mailto:support@havenconnect.com">support@havenconnect.com</a>
              </li>
            </ul>
          </div>
        </Column>

        <Column width={8}>
          <div className="card">
            <h4 className="faq" style={{ marginTop: -3 }}>
              Frequently Asked Questions
            </h4>
            <Routes>
              <Route index element={<SupportHome />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="applications" element={<ApplicationsPage />} />
              <Route path="how-it-works" element={<HowItWorksPage />} />
              <Route path="waitlist" element={<WaitListPage />} />
            </Routes>
          </div>
        </Column>
      </Row>
    </div>
  </div>
);

export default Support;
